/**
 * When a replay's ball stops, for hosts that cannot hear the scene's own
 * onRollSettled, and which waiting balls a replay must not draw twice.
 *
 * The bug (Cole, 2026-10-19, Baseball on the TV): "for home runs only, the
 * ball renders near the hole a split second before it stops rolling, so at
 * the very end there are two balls." Under a TV the phone draws no scene, so
 * its stage timed the roll itself and capped it at 6.5 s (a bound copied
 * from the Unity Studio replay, which clamps its own roll). The TV's scene
 * never capped: a 45 ft home run rolls about 7 s. At 6.5 s the phone heard
 * "settled", Baseball put the batter's ball on the green for the fielder
 * (extraBalls), and the TV drew it at the rest spot while its replay ball
 * was still rolling there. The 35 ft triple stops inside 6.5 s, which is why
 * only home runs showed it.
 *
 * Two fixes, one per side, so either an old phone or an old TV still gets
 * one of them: the phone's stage times the roll exactly as the scene does
 * (replayRollMs), and the scene never draws a waiting ball on top of the
 * live replay's resting spot (staticBallHiddenByReplay): the replay ball is
 * that ball until the scene restages.
 */
import { BALL_RADIUS_FT } from './puttMath';

/** Never announce a roll as over sooner than this (a tap-in still reads). */
export const REPLAY_ROLL_FLOOR_MS = 1100;
/** A ceiling for degenerate reads only. A 60 ft putt on a stimp 14 green
 *  rolls under 10 s (t = sqrt(d * stimp / 9)), so a real roll never hits it. */
export const REPLAY_ROLL_CEILING_MS = 12_000;

/** The scene's own stop time in ms: SceneContent's speed / decelFor, which
 *  is 2 * distance / speed for any real read. */
export function replayStopMs(speedFtS: number, distanceFt: number): number {
  if (!(speedFtS > 0.05) || !(distanceFt > 0.05)) return 0;
  return ((2 * distanceFt) / speedFtS) * 1000;
}

/**
 * How long a host that does not render the scene waits for the ball to stop:
 * the scene's own stop time, never earlier. The old 6.5 s cap ended long
 * putts early. Feed it the same speed the scene rolls with
 * (puttReplayInputs().speedFtS, or the TV event's speedMps in ft/s).
 */
export function replayRollMs(speedFtS: number, distanceFt: number): number {
  const stopMs = replayStopMs(speedFtS, distanceFt);
  return Math.min(REPLAY_ROLL_CEILING_MS, Math.max(REPLAY_ROLL_FLOOR_MS, stopMs));
}

/** Two real balls never sit closer than one diameter apart, center to
 *  center, so a waiting ball that close to the replay's rest is that ball. */
export const SAME_BALL_TOLERANCE_FT = BALL_RADIUS_FT * 2;

/**
 * Whether a waiting ball (staticBalls) is the live replay's own ball, drawn
 * by a host at its resting spot: hidden while the replay is live, so it can
 * never appear before the rolling ball gets there, and never sits doubled on
 * top of it after. Once the scene restages (live is false) it shows again.
 * `restXFt` / `restDistanceFt` are the scene's rendered rest (restX, z =
 * -distanceFt).
 */
export function staticBallHiddenByReplay(
  ball: { xFt: number; zFt: number },
  replay: { live: boolean; restXFt: number; restDistanceFt: number },
): boolean {
  if (!replay.live) return false;
  if (!Number.isFinite(ball.xFt) || !Number.isFinite(ball.zFt)) return false;
  return Math.hypot(ball.xFt - replay.restXFt, ball.zFt + replay.restDistanceFt) < SAME_BALL_TOLERANCE_FT;
}
